<template>
  <ui-menu-filter-trigger
    size="md-wide"
    :tooltip-content="
      tippyShown ? (props.disabled ? $t('conversations.filters.form.downgraded_filter_tooltip') : description) : null
    "
    data-testid="active-filter"
    :can-remove="(activeFilter || forceShowRemoveButton) && !forceHideRemoveButton"
    :can-add="activeFilter && showAddSimilarButton"
    :show-chevron="false"
    :theme="UiMenuTriggerTheme.Blue"
    :active="activeFilter && !props.disabled"
    @remove="$emit('remove')"
    @add="$emit('add')"
  >
    <template v-if="props.intelligenceIcon || props.botIcon" #icon>
      <intelligence-icon v-if="props.intelligenceIcon" class="h-[14px] shrink-0" />
      <bot-icon v-else-if="props.botIcon" class="h-[16px] shrink-0" />
    </template>

    <template #label>
      {{ filterLabel }}
    </template>

    <template v-if="showCondition" #condition>
      {{ condition }}
    </template>

    <template v-if="!props.selectedCondition.valueRequired">
      {{ props.selectedCondition.label }}
    </template>
    <img v-else-if="emojiSrc" :src="emojiSrc" class="mx-1 h-4 w-4" />
    <span
      v-else-if="getSelectedValuesLabel"
      class="truncate whitespace-nowrap text-xs font-medium"
      :class="[hasValue || props.allSelectedPlaceholder ? 'text-active-primary' : 'text-gray-50']"
    >
      {{ suffix ? $t(`${suffix}`, { value: getSelectedValuesLabel }) : getSelectedValuesLabel }}
    </span>
  </ui-menu-filter-trigger>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { UiMenuFilterTrigger, UiMenuTriggerTheme } from '@klausapp/ui-kit'
import { getTicketStatusLabel } from '@/modules/shared/TicketContent/utils/ticketStatus.util'

import i18n from '@/i18n'
import IntelligenceIcon from '@/assets/intelligence-icon.svg'
import BotIcon from '@/assets/bot.svg'
import { formatDateIntl } from '@/utils/date'
import { type Condition, type FilterItemValue, FilterValueType } from './types'

const props = defineProps<{
  tippyShown?: boolean
  description?: string
  activeFilter: boolean
  disabled: boolean
  intelligenceIcon?: boolean
  botIcon?: boolean
  filterLabel: string
  selectedCondition: Condition
  emojiSrc?: string
  value?: FilterItemValue
  valueType: FilterValueType
  allSelectedPlaceholder?: string
  forceShowRemoveButton?: boolean
  forceHideRemoveButton?: boolean
  showAddSimilarButton?: boolean
  error?: boolean
  suffix?: string
}>()

defineEmits<{
  remove: []
  add: []
}>()

const hasValue = computed(() => {
  if (Array.isArray(props.value)) return !!props.value.length

  return !!props.value
})

const showCondition = computed(() => {
  return props.selectedCondition.label && props.activeFilter
})

const condition = computed(() => {
  return props.selectedCondition.valueRequired ? props.selectedCondition.label : i18n.t('universal.equals')
})

const getSelectedValuesLabel = computed(() => {
  if (!props.value) return props.allSelectedPlaceholder || ''
  if (props.valueType === FilterValueType.Date) return formatDateIntl(props.value as string, { dateStyle: 'medium' })
  if (Array.isArray(props.value)) {
    if (!props.value.length && props.allSelectedPlaceholder) return props.allSelectedPlaceholder
    const labels = props.value.map((v) => v.label || v.value || v)
    return labels.length === 1 ? getTicketStatusLabel(labels[0]) : i18n.t('universal.items_selected', labels.length)
  }
  return props.value
})
</script>
